import { ApolloClient } from '@apollo/client';
import { keyBy } from 'lodash-es';

import {
  ILoyaltyOfferConfigsQuery,
  ILoyaltyOfferGroupsByIdsQuery,
  ILoyaltyOfferTemplatesQuery,
  ILoyaltySystemwideOffersByEngineIdQuery,
  IPlaceholderFieldsQuery,
  LoyaltyOfferConfigsDocument,
  LoyaltyOfferGroupsByIdsDocument,
  LoyaltyOfferTemplatesDocument,
  LoyaltySystemwideOffersByEngineIdDocument,
  PlaceholderFieldsDocument,
} from 'generated/sanity-graphql';
import {
  ILocaleSmartBlockContent,
  PlaceholderFieldsMap,
  TransformSmartBlockContentFn,
} from 'hooks/use-locale-smart-block-content/types';

import {
  AppPersonalizedOffer,
  EnginePersonalizedOffer,
  LoyaltyEngineId,
  SanityConfigOffer,
  SanityId,
  SanityOfferGroupHeader,
  SanitySystemwideOffer,
  buildAppPersonalizedOffer,
} from '../../types';

export const fetchPersonalizedOfferData = async ({
  client,
  uri,
  templatesIds = [],
  configOffersIds = [],
  systemwideOffersIds = [],
  offerGroupsIds = [],
}: {
  client: ApolloClient<object>;
  uri: string;
  templatesIds?: string[];
  configOffersIds?: string[];
  systemwideOffersIds?: string[];
  offerGroupsIds?: string[];
}) => {
  const [configs, templates, placeholderFields, systemwideOffers, offerGroups] = await Promise.all([
    configOffersIds.length > 0
      ? client.query<ILoyaltyOfferConfigsQuery>({
          query: LoyaltyOfferConfigsDocument,
          context: { uri },
          variables: { ids: configOffersIds },
        })
      : Promise.resolve(null),
    client.query<ILoyaltyOfferTemplatesQuery>({
      query: LoyaltyOfferTemplatesDocument,
      context: { uri },
      variables: {
        ids: templatesIds,
      },
    }),
    client.query<IPlaceholderFieldsQuery>({
      query: PlaceholderFieldsDocument,
      context: { uri },
    }),
    systemwideOffersIds.length > 0
      ? client.query<ILoyaltySystemwideOffersByEngineIdQuery>({
          query: LoyaltySystemwideOffersByEngineIdDocument,
          context: { uri },
          variables: { ids: systemwideOffersIds },
        })
      : Promise.resolve(null),
    offerGroupsIds.length > 0
      ? client.query<ILoyaltyOfferGroupsByIdsQuery>({
          query: LoyaltyOfferGroupsByIdsDocument,
          context: { uri },
          variables: { ids: offerGroupsIds },
        })
      : Promise.resolve(null),
  ]);

  const allConfigOffers = configs?.data?.allConfigOffers || [];
  const configMap = keyBy(allConfigOffers, 'loyaltyEngineId') as Record<
    LoyaltyEngineId,
    SanityConfigOffer
  >;

  const allSystemwideOffers = systemwideOffers?.data?.allSystemwideOffers || [];
  // NOTE: For offers inside Personalized Offer Groups, the engine provides `configId` values that
  // map to the *loyalty engine id* of the backing Systemwide Offer (not the Sanity `_id`).
  // We therefore key this map by `loyaltyEngineId` to support lookups by `engineOffer.configId`.
  const systemwideOffersMap = keyBy(allSystemwideOffers, 'loyaltyEngineId') as Record<
    string,
    SanitySystemwideOffer
  >;

  const allOfferGroups = offerGroups?.data?.allOfferGroups || [];
  const offerGroupsMap = (keyBy(allOfferGroups, '_id') as unknown) as Record<
    SanityId,
    SanityOfferGroupHeader
  >;

  const allOfferTemplates = templates?.data?.allOfferTemplates || [];
  const templateMap = keyBy(allOfferTemplates, 'loyaltyEngineId');

  const allPlaceholderFields = placeholderFields?.data?.allPlaceholderFields || [];
  const placeholderFieldsMap = keyBy(allPlaceholderFields, '_id') as PlaceholderFieldsMap;

  return { configMap, systemwideOffersMap, offerGroupsMap, templateMap, placeholderFieldsMap };
};

type Await<T> = T extends Promise<infer U> ? U : T;

export interface IMergePersonalizedData extends IPersonalizedData {
  engineOffer: EnginePersonalizedOffer;
  transformSmartBlockContent: TransformSmartBlockContentFn;
}
export interface IPersonalizedData {
  configMap: Await<ReturnType<typeof fetchPersonalizedOfferData>>['configMap'];
  systemwideOffersMap: Await<ReturnType<typeof fetchPersonalizedOfferData>>['systemwideOffersMap'];
  offerGroupsMap: Await<ReturnType<typeof fetchPersonalizedOfferData>>['offerGroupsMap'];
  templateMap: Await<ReturnType<typeof fetchPersonalizedOfferData>>['templateMap'];
  placeholderFieldsMap: Await<
    ReturnType<typeof fetchPersonalizedOfferData>
  >['placeholderFieldsMap'];
}

export const mergePersonalizedData = ({
  engineOffer,
  configMap,
  templateMap,
  placeholderFieldsMap,
  transformSmartBlockContent,
}: IMergePersonalizedData): AppPersonalizedOffer => {
  const { templateId, configId } = engineOffer || {};
  const config = configMap[(configId || '') as LoyaltyEngineId];
  const template = templateMap[templateId || ''];

  const { localizedImage, backgroundImage, name, description } = template || {};
  const hasLocalizedImage = Boolean(localizedImage?.locale?.app);

  const transformField = (blockContent: ILocaleSmartBlockContent | null) =>
    blockContent &&
    transformSmartBlockContent({
      blockContent,
      interpolationSourceObject: {
        engineOffer,
        template,
        config,
      },
      placeholderFieldsMap,
    });

  const baseOffer = buildAppPersonalizedOffer(config, engineOffer);
  const appPersonalizedOffer = {
    ...baseOffer,
    // Override offer config data with personalization from template
    ...(template && {
      name: transformField(name || null),
      description: transformField(description || null),
    }),
    ...(hasLocalizedImage && { localizedImage }),
    ...(backgroundImage && { backgroundImage }),
  } as const;

  return appPersonalizedOffer;
};
